import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from 'framer-motion';

// Import the RankList component
import RankList from "./RankList.tsx";

interface SocialMediaProps {
  userId: string;
  userName: string;
  onBack: () => void;
  points?: number;
}

const SocialMedia: React.FC<SocialMediaProps> = ({ userId, userName, onBack, points = 1000 }) => {
  const navigate = useNavigate();
  const [likes, setLikes] = useState<{ [key: number]: number }>({
    1: 15,
    2: 22,
    3: 8,
  });
  const [likedPosts, setLikedPosts] = useState<number[]>([]);
  const [currentView, setCurrentView] = useState<"feed" | "ranklist">("feed"); // State to toggle between Feed and Ranklist

  const feed = [
    {
      id: 1,
      user: "Alex Star",
      thumbnail: "https://images.unsplash.com/photo-1516627145497-ae696f9b7328?q=80&w=400",
      achievement: "Earned 500 IQ Points!",
      description: "Alex completed a challenging science quiz and earned 500 IQ Points!",
      comments: [
        { user: "Emma", text: "Wow, great job, Alex!" },
        { user: "Liam", text: "That's amazing!" },
      ],
    },
    {
      id: 2,
      user: "Sophie Dreamer",
      thumbnail: "https://images.unsplash.com/photo-1534528741775-53994a69daeb?q=80&w=400",
      achievement: "Top 10 in State Rank!",
      description: "Sophie ranked in the top 10 for her state in the weekly leaderboard!",
      comments: [
        { user: "Noah", text: "You're a superstar, Sophie!" },
        { user: "Mia", text: "Keep it up!" },
      ],
    },
    {
      id: 3,
      user: "Max Explorer",
      thumbnail: "https://images.unsplash.com/photo-1503023345310-bd7c1de61c7d?q=80&w=400",
      achievement: "Completed 10 Daily Tasks!",
      description: "Max finished all daily tasks for 10 days in a row!",
      comments: [
        { user: "Olivia", text: "That's dedication!" },
        { user: "Ethan", text: "Awesome work, Max!" },
      ],
    },
  ];

  const handleLike = (postId: number) => {
    if (likedPosts.includes(postId)) {
      setLikes((prev) => ({ ...prev, [postId]: prev[postId] - 1 }));
      setLikedPosts((prev) => prev.filter((id) => id !== postId));
    } else {
      setLikes((prev) => ({ ...prev, [postId]: prev[postId] + 1 }));
      setLikedPosts((prev) => [...prev, postId]);
    }
  };

  const handleCommentClick = (postId: number) => {
    console.log(`Clicked comment on post ID: ${postId}`);
  };

  return (
    <div className="socialmedia-container">
      <style>
        {`
          :root {
            --cyber-green: #a8fd2d;
            --electric-cyan: #22c1c3;
            --cosmic-pink: #ff1493;
            --midnight-black: #000000;
            --glass-bg: rgba(10, 14, 43, 0.3);
            --card-blur: 12px;
            --background-color: var(--midnight-black);
            --text-color: white;
            --card-border-color: rgba(255, 255, 255, 0.1);
            --card-text-color: white;
            --card-bg: #000000;
            --input-bg: #1a202c;
            --input-text: white;
            --input-border: #4a5568;
            --gray-text: #e5e7eb;
            --gray-text-secondary: #9ca3af;
            /* From RankList */
            --neon-green: #a8fd2d;
            --neon-cyan: #22c1c3;
            --neon-blue: #1e90ff;
            --neon-purple: #9b59b6;
            --dark-blue: #1c2526;
          }

          .theme-white {
            --background-color: #f5f5f5;
            --text-color: #333;
            --card-border-color: #d1d5db;
            --card-text-color: #333;
            --card-bg: rgba(255, 255, 255, 0.8);
            --glass-bg: rgba(255, 255, 255, 0.3);
            --input-bg: #ffffff;
            --input-text: #333;
            --input-border: #d1d5db;
            --gray-text: #4b5563;
            --gray-text-secondary: #6b7280;
          }

          .theme-pink {
            --background-color: #fff0f6;
            --text-color: #333;
            --card-border-color: #f9a8d4;
            --card-text-color: #333;
            --card-bg: rgba(255, 240, 246, 0.8);
            --glass-bg: rgba(255, 240, 246, 0.3);
            --input-bg: #ffffff;
            --input-text: #333;
            --input-border: #f9a8d4;
            --gray-text: #4b5563;
            --gray-text-secondary: #6b7280;
          }

          .socialmedia-container {
            max-width: 1000px;
            margin: 120px auto 0;
            padding: 16px;
            min-height: calc(100vh - 120px);
            background: var(--background-color);
            color: var(--text-color);
            display: flex;
            flex-direction: column;
            gap: 16px;
          }

          .view-toggle {
            display: flex;
            gap: 8px;
            justify-content: center;
            margin-bottom: 16px;
          }

          .toggle-button {
            padding: 8px 16px;
            border-radius: 8px;
            border: 1px solid var(--card-border-color);
            background: var(--glass-bg);
            backdrop-filter: blur(var(--card-blur));
            color: var(--text-color);
            font-size: 14px;
            font-weight: 500;
            cursor: pointer;
            transition: all 0.3s ease;
          }

          .toggle-button:hover {
            border-color: var(--cyber-green);
            color: var(--cyber-green);
          }

          .toggle-button.active {
            background: linear-gradient(45deg, var(--electric-cyan), var(--cyber-green));
            border-color: var(--cyber-green);
            color: white;
            box-shadow: 0 0 15px var(--cyber-green);
            transform: scale(1.05);
          }

          .feed-container {
            display: flex;
            flex-direction: column;
            gap: 16px;
            padding: 16px;
          }

          .feed-item {
            background: var(--card-bg);
            border: 1px solid var(--card-border-color);
            border-radius: 15px;
            overflow: hidden;
            transition: all 0.3s ease;
            position: relative;
            color: var(--card-text-color);
          }

          .feed-item::before {
            content: '';
            position: absolute;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            background: var(--glass-bg);
            backdrop-filter: blur(var(--card-blur));
            z-index: -1;
            border-radius: 15px;
          }

          .feed-item:hover {
            box-shadow: 0 0 15px var(--cyber-green);
            border-color: var(--cyber-green);
            transform: scale(1.02);
          }

          .feed-item:active {
            box-shadow: 0 0 25px var(--cyber-green);
            transform: scale(0.98);
          }

          .thumbnail {
            width: 100%;
            aspect-ratio: 1;
            object-fit: cover;
          }

          .item-content {
            padding: 12px;
          }

          .item-header {
            display: flex;
            align-items: center;
            gap: 12px;
            margin-bottom: 8px;
          }

          .item-user {
            font-size: 16px;
            font-weight: 600;
            color: var(--text-color);
          }

          .item-achievement {
            font-size: 18px;
            font-weight: 600;
            color: var(--cyber-green);
            margin-bottom: 8px;
          }

          .item-description {
            font-size: 14px;
            color: var(--gray-text-secondary);
            margin-bottom: 12px;
          }

          .interaction-bar {
            display: flex;
            gap: 16px;
            padding: 8px 12px;
            border-top: 1px solid var(--card-border-color);
          }

          .like-button,
          .comment-button {
            display: flex;
            align-items: center;
            gap: 8px;
            background: none;
            border: none;
            color: var(--gray-text);
            cursor: pointer;
            font-size: 14px;
            transition: color 0.3s ease;
          }

          .like-button:hover,
          .comment-button:hover {
            color: var(--cyber-green);
          }

          .like-button.liked {
            color: var(--cosmic-pink);
          }

          .like-button.liked svg {
            fill: var(--cosmic-pink);
          }

          .comments-section {
            padding: 12px;
            border-top: 1px solid var(--card-border-color);
          }

          .comment {
            font-size: 12px;
            color: var(--gray-text-secondary);
            margin-bottom: 8px;
          }

          .comment-user {
            font-weight: 600;
            color: var(--gray-text);
          }

          /* RankList styles (copied from RankList.tsx to ensure compatibility) */
          .bg-neon-green {
            background-color: var(--neon-green) !important;
          }

          .bg-dark-blue {
            background-color: var(--dark-blue) !important;
          emulate .my-rank-card {
            background-color: rgb(17, 0, 255) !important;
            border: 1px solid rgb(255, 255, 255) !important;
          }

          .scrollbar-thin {
            scrollbar-width: thin;
          }

          .scrollbar-thumb-neon-green\\/60 {
            scrollbar-color: rgba(168, 253, 45, 0.6) rgba(10, 10, 10, 0.5);
          }

          .scrollbar-track-gray-900\\/50 {
            scrollbar-color: rgba(10, 10, 10, 0.5);
          }

          .touch-manipulation {
            touch-action: manipulation;
          }

          .backdrop-blur-sm {
            backdrop-filter: blur(8px);
          }

          .backdrop-blur-lg {
            backdrop-filter: blur(12px);
          }

          @media (hover: hover) {
            .hover\\:scale-110:hover {
              transform: scale(1.1);
            }
          }

          .shadow-neon-green\\/40 {
            animation: glow 2s ease-in-out infinite;
          }

          /* Spinner Animation */
          .spinner {
            width: 40px;
            height: 40px;
            border: 4px solid rgba(168, 253, 45, 0.2);
            border-top: 4px solid var(--neon-green);
            border-radius: 50%;
            animation: spin 1s linear infinite;
          }

          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }

          @media (max-width: 768px) {
            .socialmedia-container {
              padding: 12px;
              margin-top: 100px;
              min-height: calc(100vh - 120px);
            }

            .view-toggle {
              padding: 6px 12px;
              font-size: 12px;
            }

            .feed-container {
              padding: 12px;
            }

            .item-user,
            .item-achievement {
              font-size: 14px;
            }

            .item-description,
            .interaction-bar,
            .comment {
              font-size: 12px;
            }

            .user-icon {
              width: 40px;
              height: 40px;
              font-size: 18px;
            }
          }
        `}
      </style>

      {/* Toggle buttons for Feed and Ranklist */}
      <div className="view-toggle">
        <button
          className={`toggle-button ${currentView === "feed" ? "active" : ""}`}
          onClick={() => setCurrentView("feed")}
        >
          Feed
        </button>
        <button
          className={`toggle-button ${currentView === "ranklist" ? "active" : ""}`}
          onClick={() => setCurrentView("ranklist")}
        >
          Ranklist
        </button>
      </div>

      {/* Conditional rendering of Feed or Ranklist */}
      {currentView === "feed" ? (
        <div className="feed-container">
          {feed.map((post) => (
            <div key={post.id} className="feed-item">
              <img src={post.thumbnail} alt={post.achievement} className="thumbnail" />
              <div className="item-content">
                <div className="item-header">
                  <div className="user-icon">{post.user[0]}</div>
                  <span className="item-user">{post.user}</span>
                </div>
                <h3 className="item-achievement">{post.achievement}</h3>
                <p className="item-description">{post.description}</p>
                <div className="interaction-bar">
                  <button
                    className={`like-button ${likedPosts.includes(post.id) ? "liked" : ""}`}
                    onClick={() => handleLike(post.id)}
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
                    </svg>
                    {likes[post.id]} Likes
                  </button>
                  <button className="comment-button" onClick={() => handleCommentClick(post.id)}>
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z" />
                    </svg>
                    {post.comments.length} Comments
                  </button>
                </div>
                <div className="comments-section">
                  {post.comments.map((comment, index) => (
                    <div key={index} className="comment">
                      <span className="comment-user">{comment.user}: </span>
                      {comment.text}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <RankList userId={userId} userName={userName} onBack={() => setCurrentView("feed")} />
      )}
    </div>
  );
};

export default SocialMedia;